import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Lock, Eye, Users, Globe, Heart, Camera, Trash2, MapPin } from "lucide-react";
import CountrySelect from "./CountrySelect";

const SECTION_KEYS = ["identitet","ommeg","utseende","soker","kontakt","tags","lyst","synlighet"];
const SECTION_VIS_KEYS = ["appearance","languages","looking_for","contact_with","tags","lust","private_left"];
const VIS_LEVELS = [
  { v: "only_me",   icon: Lock   },
  { v: "friends",   icon: Heart  },
  { v: "followers", icon: Users  },
  { v: "all",       icon: Globe  },
];

function MultiToggle({ options, value, onChange, max, testid }) {
  const arr = Array.isArray(value) ? value : [];
  const toggle = (v) => {
    if (arr.includes(v)) onChange(arr.filter(x => x !== v));
    else if (max && arr.length >= max) return;
    else onChange([...arr, v]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map(o => {
        const active = arr.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)}
            className={`px-2.5 py-1 rounded-sm border text-[11px] font-manrope transition-colors ${
              active
                ? "border-[var(--magenta)] bg-[var(--magenta)]/15 text-[var(--cream)]"
                : "border-white/10 text-[var(--cream-soft)] hover:border-white/30"
            }`}
          >{o}</button>
        );
      })}
      {max && (
        <span className="ml-auto font-meta text-[10px] text-[var(--cream-faint)] self-center">
          {arr.length}/{max}
        </span>
      )}
    </div>
  );
}

function PosNegPicker({ options, pos, neg, onPos, onNeg, max, testid, posLabel, negLabel }) {
  const setStateFor = (opt, side) => {
    const inPos = pos.includes(opt), inNeg = neg.includes(opt);
    if (side === "pos") {
      if (inPos) onPos(pos.filter(x => x !== opt));
      else {
        if (max && pos.length >= max) return;
        onPos([...pos, opt]);
        if (inNeg) onNeg(neg.filter(x => x !== opt));
      }
    } else {
      if (inNeg) onNeg(neg.filter(x => x !== opt));
      else {
        if (max && neg.length >= max) return;
        onNeg([...neg, opt]);
        if (inPos) onPos(pos.filter(x => x !== opt));
      }
    }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={testid}>
      <div>
        <div className="font-meta mb-2 text-[var(--cream)]">{posLabel}</div>
        <div className="flex flex-wrap gap-1.5">
          {options.map(o => (
            <button key={"p"+o} type="button" onClick={() => setStateFor(o, "pos")}
              className={`px-2.5 py-1 rounded-sm border text-[11px] font-manrope transition-colors ${
                pos.includes(o)
                  ? "border-[var(--magenta)] bg-[var(--magenta)]/15 text-[var(--cream)]"
                  : "border-white/10 text-[var(--cream-soft)] hover:border-white/30"
              }`}
            >{o}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="font-meta mb-2 text-[var(--cream)]">{negLabel}</div>
        <div className="flex flex-wrap gap-1.5">
          {options.map(o => (
            <button key={"n"+o} type="button" onClick={() => setStateFor(o, "neg")}
              className={`px-2.5 py-1 rounded-sm border text-[11px] font-manrope transition-colors ${
                neg.includes(o)
                  ? "border-red-500/60 bg-red-500/15 text-[var(--cream)]"
                  : "border-white/10 text-[var(--cream-soft)] hover:border-white/30"
              }`}
            >{o}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LustGrid({ options, states, value, onChange, stateLabels }) {
  const v = value || {};
  const setV = (opt, st) => {
    const next = { ...v };
    if (next[opt] === st) delete next[opt]; else next[opt] = st;
    onChange(next);
  };
  const stateColors = {
    liker: "border-[var(--magenta)] bg-[var(--magenta)]/15",
    nysgjerrig: "border-amber-400/60 bg-amber-400/10",
    nei: "border-red-500/60 bg-red-500/15",
  };
  return (
    <div className="space-y-1.5" data-testid="lust-grid">
      {options.map(opt => (
        <div key={opt} className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5">
          <span className="font-fraunces text-[var(--cream)] text-sm">{opt}</span>
          <div className="flex gap-1 shrink-0">
            {states.map(st => (
              <button key={st} type="button" onClick={() => setV(opt, st)}
                className={`px-2 py-1 rounded-sm border text-[10px] font-manrope transition-colors ${
                  v[opt] === st ? stateColors[st] + " text-[var(--cream)]"
                                : "border-white/10 text-[var(--cream-faint)] hover:border-white/30"
                }`}
              >{stateLabels[st] || st}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TagInput({ value, suggestions, max, onChange, placeholder, suggestionsLabel, counterText }) {
  const arr = Array.isArray(value) ? value : [];
  const [input, setInput] = useState("");
  const add = (t) => {
    t = (t || "").trim();
    if (!t || /\s/.test(t)) return;
    if (t.length > 30) return;
    if (arr.includes(t)) return;
    if (arr.length >= max) return;
    onChange([...arr, t]);
    setInput("");
  };
  const remove = (t) => onChange(arr.filter(x => x !== t));
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(input); }
  };
  return (
    <div data-testid="tag-input">
      <div className="flex flex-wrap gap-1.5 mb-3">
        {arr.map(t => (
          <span key={t} className="px-2 py-1 rounded-sm border border-[var(--magenta)]/40 bg-[var(--magenta)]/10 text-[var(--cream)] text-[11px] font-manrope inline-flex items-center gap-1.5">
            #{t}
            <button type="button" onClick={() => remove(t)} className="text-[var(--cream-soft)] hover:text-[var(--magenta)]">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
        placeholder={placeholder}
        className="input-editorial mb-3" maxLength={30} data-testid="tag-text-input" />
      <div className="font-meta text-[10px] mb-2">{suggestionsLabel}</div>
      <div className="flex flex-wrap gap-1.5">
        {suggestions.filter(s => !arr.includes(s)).slice(0, 30).map(s => (
          <button key={s} type="button" onClick={() => add(s)}
            className="px-2 py-1 rounded-sm border border-white/10 text-[var(--cream-soft)] text-[11px] font-manrope hover:border-[var(--magenta)]/60 hover:text-[var(--cream)] transition-colors">
            {s}
          </button>
        ))}
      </div>
      <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-2">{counterText}</div>
    </div>
  );
}

function VisRow({ label, value, onChange, visLabels }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-white/5">
      <span className="font-fraunces text-sm text-[var(--cream)]">{label}</span>
      <div className="flex gap-1">
        {VIS_LEVELS.map(lv => {
          const Icon = lv.icon;
          const active = value === lv.v;
          return (
            <button key={lv.v} type="button" onClick={() => onChange(lv.v)}
              title={visLabels[lv.v]}
              className={`p-1.5 rounded-sm border transition-colors ${
                active
                  ? "border-[var(--magenta)] bg-[var(--magenta)]/15 text-[var(--cream)]"
                  : "border-white/10 text-[var(--cream-soft)] hover:border-white/30"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function EditProfileModal({ user, onClose, onSaved }) {
  const { t } = useTranslation();
  const [opts, setOpts] = useState(null);
  const [section, setSection] = useState("identitet");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url || "");
  const [avatarBusy, setAvatarBusy] = useState(false);

  // Fødselsdato — ett-gangs korrigering
  const initBd = (() => {
    try {
      const d = user.birthdate ? new Date(user.birthdate) : null;
      if (!d || isNaN(d.getTime())) return { y: "", m: "", d: "" };
      return { y: String(d.getUTCFullYear()), m: String(d.getUTCMonth() + 1).padStart(2, "0"), d: String(d.getUTCDate()).padStart(2, "0") };
    } catch { return { y: "", m: "", d: "" }; }
  })();
  const [bdEditOpen, setBdEditOpen] = useState(false);
  const [bdY, setBdY] = useState(initBd.y);
  const [bdM, setBdM] = useState(initBd.m);
  const [bdD, setBdD] = useState(initBd.d);
  const [bdBusy, setBdBusy] = useState(false);
  const [bdCurrent, setBdCurrent] = useState(user.birthdate || null);
  const [bdEdited, setBdEdited] = useState(!!user.birthdate_edited);
  const bdLockedByBankid = !!(user.bankid_verified_at || user.bankid_birth_date);

  const formatBd = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "—";
      return `${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}.${d.getUTCFullYear()}`;
    } catch { return "—"; }
  };

  const saveBirthdate = async () => {
    setBdBusy(true);
    try {
      const y = parseInt(bdY, 10), m = parseInt(bdM, 10), d = parseInt(bdD, 10);
      if (!y || !m || !d) { toast.error("Fyll inn år, måned og dag."); setBdBusy(false); return; }
      const r = await api.patch("/users/me/birthdate", { birth_year: y, birth_month: m, birth_day: d });
      setBdCurrent(r.data?.birthdate || null);
      setBdEdited(true);
      setBdEditOpen(false);
      toast.success("Fødselsdato oppdatert. Endringen kan ikke gjøres om.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBdBusy(false); }
  };

  const SECTION_LABELS = t("edit_profile.sections", { returnObjects: true }) || {};
  const SECTION_VIS_LABELS = t("edit_profile.section_vis", { returnObjects: true }) || {};
  const VIS_LABELS = t("edit_profile.vis", { returnObjects: true }) || {};
  const LUST_STATE_LABELS = t("edit_profile.lust_states", { returnObjects: true }) || {};

  // lås body-scroll mens modal er åpen
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("edit_profile.choose_image_file")); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(t("edit_profile.max_5mb")); return; }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setAvatarUrl(r.data.avatar_url);
      toast.success(t("edit_profile.avatar_updated"));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAvatarBusy(false); }
  };
  const removeAvatar = async () => {
    if (!window.confirm(t("edit_profile.remove_avatar_confirm"))) return;
    setAvatarBusy(true);
    try {
      await api.delete("/me/avatar");
      setAvatarUrl("");
      toast.success(t("edit_profile.avatar_removed"));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAvatarBusy(false); }
  };

  const [f, setF] = useState({
    display_name: user.display_name || "",
    show_birthday: !!user.show_birthday,
    gender_v3: user.gender_v3 || "",
    pronoun: user.pronoun || "",
    civil_status_v3: user.civil_status_v3 || [],
    orientation_v3: user.orientation_v3 || [],
    bio_short: user.bio_short || "",
    bio_long: user.bio_long || "",
    height_cm: user.height_cm || "",
    weight_kg: user.weight_kg || "",
    body_type_v3: user.body_type_v3 || "",
    ethnicity: user.ethnicity || "",
    smoking: user.smoking || "",
    hair_color: user.hair_color || "",
    eye_color: user.eye_color || "",
    ornaments: user.ornaments || { has_piercings: false, piercings_amount: "", has_tattoos: false, tattoos_amount: "", note: "" },
    languages_v3: user.languages_v3 || [],
    looking_for_pos: user.looking_for_pos || [],
    looking_for_neg: user.looking_for_neg || [],
    contact_with_pos: user.contact_with_pos || [],
    contact_with_neg: user.contact_with_neg || [],
    age_range: user.age_range || { min: 18, max: 99 },
    tags_v3: user.tags_v3 || [],
    lust_enabled: !!user.lust_enabled,
    lust_map: user.lust_map || {},
    section_visibility: user.section_visibility || {},
    search_opt_out: !!user.search_opt_out,
    country: user.country || "NO",
    fylke: user.fylke || "",
    city: user.city || "",
  });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const [locationsData, setLocationsData] = useState({ countries: [], top_countries: ["NO","SE","DK","GB","US"], subdivisions: {}, fylker: [] });
  useEffect(() => {
    api.get("/search/locations").then(r => setLocationsData(r.data)).catch(()=>{});
  }, []);
  const activeSubdivisions = React.useMemo(() => {
    if (f.country && locationsData.subdivisions?.[f.country]) return locationsData.subdivisions[f.country];
    if (f.country === "NO") return locationsData.fylker || [];
    return [];
  }, [f.country, locationsData]);

  useEffect(() => {
    api.get("/profile/options").then(r => setOpts(r.data));
  }, []);

  const save = async () => {
    setError(""); setSaving(true);
    try {
      const payload = {
        ...f,
        height_cm: f.height_cm ? parseInt(f.height_cm) : null,
        weight_kg: f.weight_kg ? parseInt(f.weight_kg) : null,
      };
      delete payload.search_opt_out;
      await api.patch("/users/me/profile", payload);
      await api.post("/me/search-opt-out", { opt_out: !!f.search_opt_out });
      onSaved && onSaved();
      onClose();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const modalContent = !opts ? (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center">
      <div className="font-meta text-[var(--cream-soft)]">{t("edit_profile.loading")}</div>
    </div>
  ) : (
    <div
      className="fixed inset-0 z-[100] bg-black/80 flex items-stretch sm:items-center sm:p-6 overflow-y-auto"
      data-testid="edit-profile-modal"
    >
      <div className="w-full max-w-5xl mx-auto my-auto bg-[var(--noir-elev)] border border-[var(--gold)]/20 rounded-sm">
        <div
          className="flex items-center justify-between p-5 border-b border-white/10 sticky z-10 top-0"
          style={{
            background: "linear-gradient(180deg, #1A1410 0%, #141414 100%)",
          }}
        >
          <div className="font-fraunces italic text-2xl text-[var(--cream)]">{t("edit_profile.title")}</div>
          <button onClick={onClose} className="text-[var(--cream-soft)] hover:text-[var(--magenta)]" data-testid="edit-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] min-h-[60vh]">
          <nav className="border-b md:border-b-0 md:border-r border-white/10 p-3 flex md:flex-col gap-1 overflow-x-auto">
            {SECTION_KEYS.map(k => (
              <button key={k} onClick={() => setSection(k)}
                className={`text-left px-3 py-2 rounded-sm font-manrope text-xs uppercase tracking-[0.18em] whitespace-nowrap ${
                  section === k
                    ? "bg-[var(--magenta)]/15 text-[var(--cream)] border-l-2 border-[var(--magenta)]"
                    : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
                }`}
                data-testid={`edit-tab-${k}`}
              >{SECTION_LABELS[k]}</button>
            ))}
          </nav>

          <div className="p-6 space-y-6">
            {section === "identitet" && (
              <div className="space-y-5">
                <div className="border border-white/10 rounded-sm p-4 flex items-center gap-5" data-testid="avatar-section">
                  <div className="w-20 h-20 rounded-sm overflow-hidden bg-[var(--noir)] border border-white/10 shrink-0 flex items-center justify-center">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="" className="w-full h-full object-cover" data-testid="avatar-preview" />
                    ) : (
                      <Camera className="h-7 w-7 text-[var(--cream-faint)]" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-meta mb-1 text-[var(--cream)]">{t("edit_profile.avatar")}</div>
                    <div className="font-meta text-[10px] text-[var(--cream-faint)] mb-3 leading-relaxed">
                      {t("edit_profile.avatar_hint")}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <label className={`btn-ghost inline-flex items-center gap-2 cursor-pointer ${avatarBusy ? "opacity-50 pointer-events-none" : ""}`} data-testid="avatar-upload-label">
                        <Camera className="h-3.5 w-3.5" />
                        {avatarUrl ? t("edit_profile.change_image") : t("edit_profile.upload_image")}
                        <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} disabled={avatarBusy} data-testid="avatar-upload-input" />
                      </label>
                      {avatarUrl && (
                        <button type="button" onClick={removeAvatar} disabled={avatarBusy}
                          className="btn-ghost inline-flex items-center gap-2 text-[var(--magenta)] border-[var(--magenta)]/40" data-testid="avatar-remove">
                          <Trash2 className="h-3.5 w-3.5" /> {t("edit_profile.remove")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {(user.is_creator && user.creator_approved) || ["admin", "owner"].includes(user.role) ? (
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.display_name")}</div>
                    <input value={f.display_name} onChange={e => set("display_name", e.target.value)}
                           maxLength={30} className="input-editorial" data-testid="display-name" />
                    <div className="font-meta text-[10px] mt-1">{t("edit_profile.display_name_hint")}</div>
                  </div>
                ) : null}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.gender")}</div>
                    <select value={f.gender_v3} onChange={e => set("gender_v3", e.target.value)} className="input-editorial" data-testid="gender">
                      <option value="">—</option>
                      {opts.gender.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <div className="font-meta text-[10px] mt-1">{t("edit_profile.gender_hint")}</div>
                  </div>
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.pronoun")}</div>
                    <select value={f.pronoun} onChange={e => set("pronoun", e.target.value)} className="input-editorial" data-testid="pronoun">
                      <option value="">—</option>
                      {opts.pronoun.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <div className="font-meta mb-2">{t("edit_profile.civil_status", { max: opts.limits.civil_status_max })}</div>
                  <MultiToggle options={opts.civil_status} value={f.civil_status_v3} max={opts.limits.civil_status_max} onChange={v => set("civil_status_v3", v)} testid="civil-status" />
                </div>
                <div>
                  <div className="font-meta mb-2">{t("edit_profile.orientation", { max: opts.limits.orientation_max })}</div>
                  <MultiToggle options={opts.orientation} value={f.orientation_v3} max={opts.limits.orientation_max} onChange={v => set("orientation_v3", v)} testid="orientation" />
                </div>
                <div className="border border-white/10 rounded-sm p-4 space-y-3" data-testid="birthdate-block">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-meta text-[var(--cream)]">Fødselsdato</div>
                      <div className="font-fraunces italic text-lg text-[var(--cream)]" data-testid="birthdate-current">{formatBd(bdCurrent)}</div>
                    </div>
                    {!bdEditOpen && !bdEdited && !bdLockedByBankid && (
                      <button type="button" onClick={() => setBdEditOpen(true)}
                        className="btn-ghost text-xs" data-testid="birthdate-edit-btn">
                        Korriger
                      </button>
                    )}
                  </div>
                  {bdLockedByBankid ? (
                    <div className="font-manrope text-[11px] text-[var(--cream-soft)] border-l-2 border-[var(--magenta)] pl-3">
                      Låst av BankID-verifisering. Kontakt support hvis den er feil.
                    </div>
                  ) : bdEdited ? (
                    <div className="font-manrope text-[11px] text-[var(--cream-soft)] border-l-2 border-[var(--magenta)] pl-3">
                      Fødselsdato er allerede korrigert én gang. Kontakt support hvis den fortsatt er feil.
                    </div>
                  ) : bdEditOpen ? (
                    <div className="space-y-2">
                      <div className="font-manrope text-[11px] text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3">
                        Kan kun endres én gang. Etter lagring er den låst.
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <input type="number" placeholder="dd" min="1" max="31" value={bdD} onChange={e => setBdD(e.target.value)}
                          className="input-editorial text-center" data-testid="birthdate-day" />
                        <input type="number" placeholder="mm" min="1" max="12" value={bdM} onChange={e => setBdM(e.target.value)}
                          className="input-editorial text-center" data-testid="birthdate-month" />
                        <input type="number" placeholder="åååå" min="1925" max="2020" value={bdY} onChange={e => setBdY(e.target.value)}
                          className="input-editorial text-center" data-testid="birthdate-year" />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button type="button" onClick={saveBirthdate} disabled={bdBusy}
                          className="btn-magenta text-xs" data-testid="birthdate-save">
                          {bdBusy ? "Lagrer…" : "Lagre (låser)"}
                        </button>
                        <button type="button" onClick={() => { setBdEditOpen(false); setBdY(initBd.y); setBdM(initBd.m); setBdD(initBd.d); }}
                          className="btn-ghost text-xs" data-testid="birthdate-cancel">
                          Avbryt
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
                <label className="flex items-center gap-2 font-manrope text-sm text-[var(--cream-soft)]">
                  <input type="checkbox" checked={f.show_birthday} onChange={e => set("show_birthday", e.target.checked)} data-testid="show-birthday" />
                  {t("edit_profile.show_birthday")}
                </label>
                <label className="flex items-start gap-2 font-manrope text-sm text-[var(--cream-soft)]" data-testid="search-opt-out-label">
                  <input type="checkbox" checked={!!f.search_opt_out}
                         onChange={e => set("search_opt_out", e.target.checked)}
                         className="mt-1" data-testid="search-opt-out" />
                  <span>
                    {t("edit_profile.search_opt_out")}
                    <div className="font-meta text-[10px] mt-0.5 text-[var(--cream-faint)] leading-relaxed">
                      {t("edit_profile.search_opt_out_hint")}
                    </div>
                  </span>
                </label>
                <div>
                  <div className="font-meta mb-2">{t("edit_profile.languages", { max: opts.limits.languages_max })}</div>
                  <MultiToggle options={opts.languages} value={f.languages_v3} max={opts.limits.languages_max} onChange={v => set("languages_v3", v)} testid="languages" />
                </div>

                {/* Lokasjon: land + fylke/region + by */}
                <div className="border border-white/10 rounded-sm p-4 space-y-4" data-testid="location-section">
                  <div className="font-meta inline-flex items-center gap-2 text-[var(--cream)]">
                    <MapPin className="h-3.5 w-3.5" /> {t("country_select.label", "Land")}
                  </div>
                  <CountrySelect
                    value={f.country}
                    onChange={(iso) => { set("country", iso || "NO"); set("fylke", ""); }}
                    allCountries={locationsData.countries || []}
                    topCountries={locationsData.top_countries || ["NO","SE","DK","GB","US"]}
                    allowEmpty={false}
                    testIdPrefix="edit-country"
                  />
                  {activeSubdivisions.length > 0 && (
                    <div>
                      <div className="font-meta mb-2">
                        {f.country === "SE" ? t("country_select.fylke_se")
                          : f.country === "DK" ? t("country_select.fylke_dk")
                          : f.country === "NO" ? t("country_select.fylke_no")
                          : t("country_select.fylke_other")}
                      </div>
                      <select value={f.fylke} onChange={(e) => set("fylke", e.target.value)} className="input-editorial" data-testid="edit-fylke">
                        <option value="">—</option>
                        {activeSubdivisions.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <div className="font-meta mb-2">{t("country_select.city", "By/sted")}</div>
                    <input value={f.city} onChange={(e) => set("city", e.target.value)} maxLength={60} className="input-editorial" data-testid="edit-city" />
                  </div>
                </div>
              </div>
            )}

            {section === "ommeg" && (
              <div className="space-y-5">
                <div>
                  <div className="font-meta mb-2">{t("edit_profile.short_bio")}</div>
                  <textarea value={f.bio_short} onChange={e => set("bio_short", e.target.value)}
                            rows={2} maxLength={280} className="input-editorial" data-testid="bio-short" />
                  <div className="font-meta text-[10px] mt-1">{f.bio_short.length}/280</div>
                </div>
                <div>
                  <div className="font-meta mb-2">{t("edit_profile.long_bio")}</div>
                  <textarea value={f.bio_long} onChange={e => set("bio_long", e.target.value)}
                            rows={10} maxLength={8000} className="input-editorial font-fraunces" data-testid="bio-long" />
                  <div className="font-meta text-[10px] mt-1">{f.bio_long.length}/8000</div>
                </div>
              </div>
            )}

            {section === "utseende" && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.height_cm")}</div>
                    <input type="number" min="80" max="260" value={f.height_cm} onChange={e => set("height_cm", e.target.value)} className="input-editorial" data-testid="height" />
                  </div>
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.weight_kg")}</div>
                    <input type="number" min="30" max="300" value={f.weight_kg} onChange={e => set("weight_kg", e.target.value)} className="input-editorial" data-testid="weight" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.body_type")}</div>
                    <select value={f.body_type_v3} onChange={e => set("body_type_v3", e.target.value)} className="input-editorial" data-testid="body-type">
                      <option value="">—</option>
                      {opts.body_type.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.ethnicity")}</div>
                    <select value={f.ethnicity} onChange={e => set("ethnicity", e.target.value)} className="input-editorial" data-testid="ethnicity">
                      <option value="">—</option>
                      {opts.ethnicity.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.hair_color")}</div>
                    <input value={f.hair_color} onChange={e => set("hair_color", e.target.value)} className="input-editorial" />
                  </div>
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.eye_color")}</div>
                    <input value={f.eye_color} onChange={e => set("eye_color", e.target.value)} className="input-editorial" />
                  </div>
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.smoking")}</div>
                    <select value={f.smoking} onChange={e => set("smoking", e.target.value)} className="input-editorial" data-testid="smoking">
                      <option value="">—</option>
                      {opts.smoking.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div className="border border-white/10 rounded-sm p-4">
                  <div className="font-meta mb-3 text-[var(--cream)]">{t("edit_profile.ornaments")}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <label className="flex items-center gap-2 font-manrope text-sm">
                      <input type="checkbox" checked={!!f.ornaments?.has_piercings}
                        onChange={e => set("ornaments", { ...f.ornaments, has_piercings: e.target.checked })}
                        data-testid="has-piercings" />
                      {t("edit_profile.piercings")}
                    </label>
                    {f.ornaments?.has_piercings && (
                      <select value={f.ornaments?.piercings_amount || ""}
                        onChange={e => set("ornaments", { ...f.ornaments, piercings_amount: e.target.value })}
                        className="input-editorial">
                        <option value="">{t("edit_profile.amount_dash")}</option>
                        {opts.ornament_amount.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                    <label className="flex items-center gap-2 font-manrope text-sm">
                      <input type="checkbox" checked={!!f.ornaments?.has_tattoos}
                        onChange={e => set("ornaments", { ...f.ornaments, has_tattoos: e.target.checked })}
                        data-testid="has-tattoos" />
                      {t("edit_profile.tattoos")}
                    </label>
                    {f.ornaments?.has_tattoos && (
                      <select value={f.ornaments?.tattoos_amount || ""}
                        onChange={e => set("ornaments", { ...f.ornaments, tattoos_amount: e.target.value })}
                        className="input-editorial">
                        <option value="">{t("edit_profile.amount_dash")}</option>
                        {opts.ornament_amount.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                  </div>
                  <textarea value={f.ornaments?.note || ""} maxLength={240}
                    onChange={e => set("ornaments", { ...f.ornaments, note: e.target.value })}
                    placeholder={t("edit_profile.ornaments_note")}
                    rows={2} className="input-editorial" />
                </div>
              </div>
            )}

            {section === "soker" && (
              <div>
                <div className="font-meta mb-3">{t("edit_profile.looking_for_label")}</div>
                <PosNegPicker options={opts.looking_for}
                  pos={f.looking_for_pos} neg={f.looking_for_neg}
                  max={opts.limits.looking_for_max}
                  onPos={v => set("looking_for_pos", v)} onNeg={v => set("looking_for_neg", v)}
                  testid="looking-for"
                  posLabel={t("edit_profile.looking")} negLabel={t("edit_profile.not_looking")} />
              </div>
            )}

            {section === "kontakt" && (
              <div className="space-y-5">
                <div>
                  <div className="font-meta mb-3">{t("edit_profile.contact_with_label")}</div>
                  <PosNegPicker options={opts.contact_with}
                    pos={f.contact_with_pos} neg={f.contact_with_neg}
                    onPos={v => set("contact_with_pos", v)} onNeg={v => set("contact_with_neg", v)}
                    testid="contact-with"
                    posLabel={t("edit_profile.looking")} negLabel={t("edit_profile.not_looking")} />
                </div>
                <div className="grid grid-cols-2 gap-3 max-w-sm">
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.age_from")}</div>
                    <input type="number" min="18" max="99" value={f.age_range?.min ?? 18}
                      onChange={e => set("age_range", { ...f.age_range, min: parseInt(e.target.value) || null })}
                      className="input-editorial" data-testid="age-min" />
                  </div>
                  <div>
                    <div className="font-meta mb-2">{t("edit_profile.age_to")}</div>
                    <input type="number" min="18" max="99" value={f.age_range?.max ?? 99}
                      onChange={e => set("age_range", { ...f.age_range, max: parseInt(e.target.value) || null })}
                      className="input-editorial" data-testid="age-max" />
                  </div>
                </div>
              </div>
            )}

            {section === "tags" && (
              <TagInput value={f.tags_v3} suggestions={opts.tag_suggestions}
                max={opts.limits.tags_max} onChange={v => set("tags_v3", v)}
                placeholder={t("edit_profile.tag_placeholder")}
                suggestionsLabel={t("edit_profile.suggestions_click")}
                counterText={t("edit_profile.tags_counter", { count: (f.tags_v3 || []).length, max: opts.limits.tags_max })} />
            )}

            {section === "lyst" && (
              <div className="space-y-4">
                <label className="flex items-center gap-2 font-manrope text-sm">
                  <input type="checkbox" checked={f.lust_enabled} onChange={e => set("lust_enabled", e.target.checked)} data-testid="lust-enabled" />
                  {t("edit_profile.lust_show_toggle")}
                </label>
                <div className="font-meta text-[10px] text-[var(--cream-faint)]">
                  {t("edit_profile.lust_intro")}
                </div>
                <LustGrid options={opts.lust} states={opts.lust_states}
                  value={f.lust_map} onChange={v => set("lust_map", v)}
                  stateLabels={LUST_STATE_LABELS} />
              </div>
            )}

            {section === "synlighet" && (
              <div>
                <div className="font-meta mb-3">{t("edit_profile.vis_intro")}</div>
                <div className="border border-white/10 rounded-sm p-4">
                  {SECTION_VIS_KEYS.map(k => (
                    <VisRow key={k} label={SECTION_VIS_LABELS[k] || k}
                      value={f.section_visibility?.[k] || opts.default_section_visibility[k]}
                      onChange={v => set("section_visibility", { ...f.section_visibility, [k]: v })}
                      visLabels={VIS_LABELS} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between p-5 border-t border-white/10 sticky bottom-0 bg-[var(--noir-elev)]">
          {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3">{error}</div>}
          <div className="flex gap-3 ml-auto">
            <button onClick={onClose} className="btn-ghost" data-testid="edit-cancel">{t("edit_profile.cancel")}</button>
            <button onClick={save} disabled={saving} className="btn-magenta" data-testid="edit-save">
              {saving ? t("edit_profile.saving") : t("edit_profile.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
